import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faBug, faLightbulb, faSignOutAlt, faInbox, faBullhorn, faPencil, faPlaneDeparture, faHeadset } from '@fortawesome/free-solid-svg-icons';
import { NhsAuthHelper } from './index.js';
import { useAuth } from './index.js';
import { useNoticeboardContext } from '../noticeboard/index.js';
import { IdentityService } from '../fetcher/IdentityService.js';
import { SessionStorage } from '../fetcher/SessionStorage.js';
import { Table, createTextColumn, createDateColumn, createEnumColumn, useTableState } from '../table/index.js';
import { IssueViewModal, BugReportModal, FeatureRequestModal, SetAvailabilityModal } from '../common/index.js';
import { availabilityLabel, isOutOfOffice } from '../utils/outOfOffice.js';
import { 
  IssueTypeEnum, 
  IssueStatusEnum, 
  getIssueTypeByValue, 
  getIssueStatusByValue, 
  generateIssueCode 
} from '../enums/IssueEnums.js';
import { MAIN_TAB_PAGE_LABEL, medPromptBrowserTitle } from '../common/mainAppPageLabels.js';
import StatusBadge from '../components/StatusBadge.js';
import '@medprompt/shared/src/common/CommonStyles.css';
import '../common/TabPageTitle.css';
import './UserView.css';

const DROP_IN_SESSIONS = [
  {
    id: 'monday',
    day: 'Monday',
    weekday: 1,
    startMinutes: 14 * 60,
    endMinutes: 15 * 60,
    timeLabel: '2–3pm',
    href: 'https://teams.microsoft.com/meet/358457479504408?p=0xXeTmI6OoW4T2rEOV',
  },
  {
    id: 'thursday',
    day: 'Thursday',
    weekday: 4,
    startMinutes: 14 * 60,
    endMinutes: 15 * 60,
    timeLabel: '2–3pm',
    href: 'https://teams.microsoft.com/meet/338567217880603?p=tdFTDEJzmnbxfnXa1g',
  },
];

const LONDON_WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function londonClock(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return {
    weekday: LONDON_WEEKDAYS[part('weekday')],
    minutes: Number(part('hour')) * 60 + Number(part('minute')),
  };
}

function isDropInLive(session, clock) {
  return clock.weekday === session.weekday
    && clock.minutes >= session.startMinutes
    && clock.minutes < session.endMinutes;
}

function nextDropInSession(clock) {
  const nowPoint = clock.weekday * 24 * 60 + clock.minutes;
  return DROP_IN_SESSIONS.reduce((soonest, session) => {
    let delta = session.weekday * 24 * 60 + session.startMinutes - nowPoint;
    if (delta <= 0) {
      delta += 7 * 24 * 60;
    }
    if (!soonest || delta < soonest.delta) {
      return { session, delta };
    }
    return soonest;
  }, null).session;
}

function DropInSupport() {
  const [clock, setClock] = useState(() => londonClock(new Date()));

  useEffect(() => {
    const timer = window.setInterval(() => setClock(londonClock(new Date())), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const liveSession = DROP_IN_SESSIONS.find((session) => isDropInLive(session, clock));
  const upcoming = nextDropInSession(clock);

  return (
    <section className="drop-in-support" aria-label="Drop-in support">
      <div className="drop-in-support-intro">
        <h2 className="drop-in-support-heading">
          <FontAwesomeIcon icon={faHeadset} />
          Drop-in support
        </h2>
        <p className={`drop-in-status${liveSession ? ' drop-in-status--live' : ''}`}>
          {liveSession
            ? `${liveSession.day} drop-in is live now. Times are UK.`
            : `Next session: ${upcoming.day} at 2pm. Times are UK.`}
        </p>
      </div>
      <div className="drop-in-sessions">
        {DROP_IN_SESSIONS.map((session) => {
          const live = liveSession?.id === session.id;
          return (
            <a
              key={session.id}
              className={`drop-in-session${live ? ' drop-in-session--live' : ''}`}
              href={session.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Join the ${session.day} drop-in support session on Microsoft Teams`}
            >
              <span className="drop-in-session-when">
                <span className="drop-in-session-day">{session.day}s</span>
                <span className="drop-in-session-time">{session.timeLabel}</span>
                {live && <span className="drop-in-live-badge">Live</span>}
              </span>
              <span className="drop-in-join">{live ? 'Join now' : 'Join'}</span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

const UserViewController = ({ titleActions = null }) => {
  const { user: contextUser, logout } = useAuth();
  const { openNoticeboard } = useNoticeboardContext();

  // Local user state that can be updated if the data changes elsewhere
  const [user, setUser] = useState(contextUser);
  
  // Clinic data state
  const [allClinics, setAllClinics] = useState([]);
  
  // Issues state
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(true);
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportType, setReportType] = useState('BUG'); // 'BUG' or 'FEATURE'
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [identityService] = useState(() => new IdentityService());

  // Table state with session storage persistence
  const {
    sortState: tableSortState,
    setSortState: setTableSortState,
    filterValues: tableFilterValues,
    setFilterValues: setTableFilterValues,
    disabledFilters: tableDisabledFilters,
    setDisabledFilters: setTableDisabledFilters,
    paginationState: tablePaginationState,
    setPaginationState: setTablePaginationState
  } = useTableState('UserIssuesTable');

  // Keep local state in sync with the context value
  useEffect(() => {
    setUser(contextUser);
  }, [contextUser]);

  // Pull freshest user data from session storage
  const refreshUserFromStorage = useCallback(() => {
    const stored = sessionStorage.getItem('user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch (_) {
        // Ignore parse errors – keep existing user state
      }
    }
  }, []);

  // Listen for custom events or storage changes that indicate user data updates
  useEffect(() => {
    const handleUserUpdate = (e) => {
      refreshUserFromStorage();
    };

    // Fetch latest user details on component mount
    refreshUserFromStorage();

    window.addEventListener('userDataUpdated', handleUserUpdate);
    window.addEventListener('storage', handleUserUpdate);

    return () => {
      window.removeEventListener('userDataUpdated', handleUserUpdate);
      window.removeEventListener('storage', handleUserUpdate);
    };
  }, [refreshUserFromStorage]);

  // Fetch clinic data and user issues
  useEffect(() => {
    identityService.fetchClinics()
      .then((clinicsResponse) => {
        setAllClinics(clinicsResponse || []);
      })
      .catch((error) => {
        console.error('Failed to fetch clinic data:', error);
      });

    fetchUserIssues();
  }, [identityService]);

  // Fetch user issues
  const fetchUserIssues = async () => {
    setIssuesLoading(true);
    try {
      const issuesData = await identityService.fetchUserIssues();
      setIssues(issuesData);
    } catch (error) {
      console.error('Failed to fetch user issues:', error);
    } finally {
      setIssuesLoading(false);
    }
  };

  const persistSessionUser = (patch) => {
    const session = new SessionStorage();
    const stored = session.getUserData();
    if (!stored) {
      return;
    }
    session.setUserData({ ...stored, ...patch });
  };

  const startNameEdit = () => {
    setNameDraft(user?.name || '');
    setNameError(null);
    setEditingName(true);
  };

  const cancelNameEdit = () => {
    setEditingName(false);
    setNameError(null);
  };

  const saveName = async () => {
    const nextName = nameDraft.trim();
    if (!nextName) {
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      await identityService.updateCurrentUser({ name: nextName });
      persistSessionUser({ name: nextName });
      setEditingName(false);
    } catch (error) {
      console.error('Failed to update name:', error);
      setNameError('Could not update name');
    } finally {
      setNameSaving(false);
    }
  };

  const saveAvailability = async ({ markOutOfOffice, returnsAt }) => {
    await identityService.updateCurrentUser({ markOutOfOffice, returnsAt });
    persistSessionUser({
      outOfOffice: markOutOfOffice ? { returnsAt: returnsAt ?? null } : null,
    });
  };

  // Set document title when component mounts
  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.user);
  }, []);

  const handleRegisterNhsLogin = () => {
    if (!user?.email) {
      console.error('Cannot start NHS onboarding – user email not available');
      return;
    }
    NhsAuthHelper.startAuth({ purpose: 'onboard', email: user.email });
  };

  const handleReportBug = () => {
    setReportType('BUG');
    setReportModalOpen(true);
  };

  const handleRequestFeature = () => {
    setReportType('FEATURE');
    setReportModalOpen(true);
  };

  const handleBugReportModalClose = () => {
    setReportType('BUG');
    setReportModalOpen(false);
  };

  const handleFeatureRequestModalClose = () => {
    setReportType('FEATURE');
    setReportModalOpen(false);
  };

  const handleIssueSubmit = async (issueData) => {
    try {
      // Make the API call to submit issue report
      const authBaseUrl = process.env.REACT_APP_IDENTITY_SERVER_URL;
        
      const response = await fetch(`${authBaseUrl}/api/issue/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(issueData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Issue submitted successfully');
      // Refetch issues after successful submission
      await fetchUserIssues();
      return true;
    } catch (error) {
      console.error('Error submitting issue:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  const handleRowClick = (issue) => {
    setSelectedIssue(issue);
    setViewModalOpen(true);
  };

  const handleViewModalClose = () => {
    setViewModalOpen(false);
    setSelectedIssue(null);
  };

  // Column definitions
  const columns = useMemo(() => [
    // Issue Code Column
    createTextColumn(
      'issueCode',
      'Issue Code',
      (issue) => generateIssueCode(issue.issueNumber)
    ),

    // Type Column with icons
    createEnumColumn(
      'type',
      'Type',
      (issue) => issue.type,
      Object.keys(IssueTypeEnum),
      {
        renderer: (value) => {
          const issueType = getIssueTypeByValue(value);
          const icon = value === IssueTypeEnum.BUG.value ? faBug : faLightbulb;
          return issueType ? (
            <div className="issue-type-cell">
              <FontAwesomeIcon icon={icon} className="issue-type-icon" />
              <span>{issueType.displayName}</span>
            </div>
          ) : value;
        },
        sortable: true,
        compareFn: (a, b) => {
          const aType = getIssueTypeByValue(a.type)?.displayName || a.type || '';
          const bType = getIssueTypeByValue(b.type)?.displayName || b.type || '';
          return aType.localeCompare(bType);
        }
      }
    ),

    // Description Column
    createTextColumn(
      'description',
      'Description',
      (issue) => issue.description,
      {
        renderer: (value) => (
          <div className="description-cell" title={value}>
            {value && value.length > 100 ? `${value.substring(0, 100)}...` : value}
          </div>
        )
      }
    ),

    // Status Column
    createEnumColumn(
      'status',
      'Status',
      (issue) => issue.status,
      Object.keys(IssueStatusEnum),
      {
        renderer: (value) => {
          const status = getIssueStatusByValue(value);
          return status ? (
            <StatusBadge
              label={status.displayName}
              colorFamily={status.colorFamily || 'gray'}
              size="normal"
            />
          ) : value;
        },
        sortable: true,
        compareFn: (a, b) => {
          const aStatus = getIssueStatusByValue(a.status)?.displayName || a.status || '';
          const bStatus = getIssueStatusByValue(b.status)?.displayName || b.status || '';
          return aStatus.localeCompare(bStatus);
        }
      }
    ),

    // Created Date Column
    createDateColumn(
      'createdAt',
      'Created',
      (issue) => issue.createdAt
    ),

    // Completed Date Column
    createDateColumn(
      'completedAt',
      'Completed',
      (issue) => issue.completedAt,
      {
        renderer: (value) => value ? new Date(value).toLocaleDateString() : '-'
      }
    )
  ], []);

  return (
    <div className="user-view-wrapper">
      {/* Issues Section - Left 70% */}
      <div className="user-issues-section">
        <div className="header">
          <div className="header-title-text">
            <div className="header-title-with-help">
              <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.user}</h1>
              {titleActions}
            </div>
          </div>
          <div className="header-buttons" />
        </div>

        <DropInSupport />
        
        {issuesLoading ? (
          <div className="loading-container">Loading your issues...</div>
        ) : (
          <div className="user-issues-table-area">
            <Table
              id="UserIssuesTable"
              data={issues}
              columns={columns}
              getRowKey={(issue) => issue.issueId}
              onRowClick={handleRowClick}
              sortState={tableSortState}
              onSortChange={setTableSortState}
              filterValues={tableFilterValues}
              onFilterChange={setTableFilterValues}
              disabledFilters={tableDisabledFilters}
              onDisabledFiltersChange={setTableDisabledFilters}
              paginationState={tablePaginationState}
              onPaginationChange={setTablePaginationState}
              emptyMessage={
                <div className="empty-issues-state">
                  <FontAwesomeIcon icon={faInbox} size="3x" className="empty-icon" />
                  <p className="empty-title">You haven't reported any issues yet</p>
                  <p className="empty-subtitle">Use the buttons on the right to report bugs or request features</p>
                </div>
              }
            />
          </div>
        )}
      </div>

      {/* User Profile Section - Right 30% */}
      <div className="user-profile-section">
        <div className="user-profile">
          <div className="profile-header">
            <div className="profile-photo-container">
              <div className="profile-photo">
                <span className="profile-initials">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
            </div>
            <h2 className="profile-title">Profile</h2>
          </div>
          
          <div className="user-info">
            <div className="info-row">
              <label>Name:</label>
              {editingName ? (
                <div className="profile-name-edit">
                  <input
                    type="text"
                    className="profile-name-input"
                    value={nameDraft}
                    onChange={(event) => setNameDraft(event.target.value)}
                    disabled={nameSaving}
                    aria-label="Name"
                  />
                  <div className="profile-name-edit-actions">
                    <button
                      type="button"
                      className="profile-name-save"
                      onClick={saveName}
                      disabled={nameSaving || !nameDraft.trim()}
                    >
                      {nameSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="profile-name-cancel"
                      onClick={cancelNameEdit}
                      disabled={nameSaving}
                    >
                      Cancel
                    </button>
                  </div>
                  {nameError && <p className="profile-field-error">{nameError}</p>}
                </div>
              ) : (
                <span className="profile-name-display">
                  <span>{user?.name || 'Not available'}</span>
                  <button
                    type="button"
                    className="profile-icon-button"
                    onClick={startNameEdit}
                    title="Edit name"
                  >
                    <FontAwesomeIcon icon={faPencil} />
                  </button>
                </span>
              )}
            </div>
            <div className="info-row">
              <label>Email:</label>
              <span>{user?.email || 'Not available'}</span>
            </div>
            <div className="info-row">
              <label>Availability:</label>
              <span className={`profile-availability${isOutOfOffice(user) ? ' profile-availability--away' : ''}`}>
                {isOutOfOffice(user) && <FontAwesomeIcon icon={faPlaneDeparture} />}
                {isOutOfOffice(user) && user?.outOfOffice?.returnsAt == null
                  ? 'Out of office, no return date'
                  : availabilityLabel(user?.outOfOffice)}
              </span>
            </div>
            {user?.nhsUserId && (
              <div className="info-row">
                <label>NHS User ID:</label>
                <span>{user.nhsUserId}</span>
              </div>
            )}
            {allClinics.length > 0 && (
              <div className="clinic-pills-section">
                <label className="clinic-pills-label">Clinics</label>
                <div className="clinic-pills">
                  {allClinics.map((clinic) => {
                    const isSelected = clinic.clinicId === SessionStorage.getSelectedClinicId();
                    return (
                      <div key={clinic.clinicId} className={`clinic-pill${isSelected ? ' clinic-pill--selected' : ''}`}>
                        <span className="clinic-pill-name">{clinic.clinicName}</span>
                        <em className="clinic-pill-id">{clinic.clinicId}</em>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <div className="profile-actions">
            {!user?.nhsUserId && (
              <button className="profile-button nhs-button" onClick={handleRegisterNhsLogin}>
                <FontAwesomeIcon icon={faLink} />
                Link your NHS Login
              </button>
            )}
            <button className="profile-button availability-button" onClick={() => setAvailabilityOpen(true)}>
              <FontAwesomeIcon icon={faPlaneDeparture} />
              Out of office
            </button>
            <button className="profile-button notices-button" onClick={openNoticeboard}>
              <FontAwesomeIcon icon={faBullhorn} />
              Notices
            </button>
            <button className="profile-button bug-button" onClick={handleReportBug}>
              <FontAwesomeIcon icon={faBug} />
              Report a problem
            </button>
            <button className="profile-button feature-button" onClick={handleRequestFeature}>
              <FontAwesomeIcon icon={faLightbulb} />
              Request a feature
            </button>
            <button className="profile-button logout-button" onClick={logout}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SetAvailabilityModal
        isOpen={availabilityOpen}
        onClose={() => setAvailabilityOpen(false)}
        onSave={async (payload) => {
          await saveAvailability(payload);
          setAvailabilityOpen(false);
        }}
        user={user}
      />
      <IssueViewModal
        isOpen={viewModalOpen}
        onClose={handleViewModalClose}
        issue={selectedIssue}
      />
      
      <BugReportModal
        isOpen={reportModalOpen && reportType === 'BUG'}
        onClose={handleBugReportModalClose}
        onSubmit={handleIssueSubmit}
        viewControllerName="User View"
      />

      <FeatureRequestModal
        isOpen={reportModalOpen && reportType === 'FEATURE'}
        onClose={handleFeatureRequestModalClose}
        onSubmit={handleIssueSubmit}
      />
    </div>
  );
};

export default UserViewController;
